import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import type { TaxonomyCampaign } from '@/entities/taxonomy-campaign';

interface CampaignActionMenuProps {
  campaign: TaxonomyCampaign;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * The campaign row's overflow menu.
 *
 * Same `stopPropagation` pair as {@link DatasetActionMenu}, for the same reason
 * and one more: the campaign row's own click TOGGLES the group, so without these
 * opening the menu would also collapse the rows the user is about to act on.
 */
export function CampaignActionMenu({
  campaign,
  canManage,
  onEdit,
  onDelete,
}: CampaignActionMenuProps) {
  const { t } = useTranslation('knowledge');
  const [open, setOpen] = useState(false);

  if (!canManage) {
    return null;
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- see the note above: this only cancels a bubble the row would otherwise act on.
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.generationList.rowActions', { name: campaign.name })}
            className="size-9 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onSelect={onEdit}>
            <PencilIcon className="mr-2 size-4" />
            {t('taxonomy.generationList.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onDelete} variant="destructive">
            <Trash2Icon className="mr-2 size-4" />
            {t('taxonomy.generationList.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
